/**
 * Процессуальные роли в расшифровке судебного заседания — по самому тексту.
 *
 * Разметка голосов отдаёт «Говорящий 1», «Говорящий 2»; кто из них судья, а кто
 * истец, она не знает. На 07.10.2026 этого не делал ни один русский сервис
 * расшифровки (ближайший конкурент пишет прямо: «Роли «судья» или «истец» сами
 * не определяются»), и человек подписывал голоса руками.
 *
 * Заседание, однако, называет роли само: председательствующий обращается к
 * участникам по их положению — «Ответчик, ваше мнение?», «Государственный
 * обвинитель, приступайте к допросу» — и следующим говорит тот, к кому
 * обратились. Отсюда правило: реплика судьи кончается обращением → голос,
 * который ответил, получает голос за эту роль. К этому добавлено то, что
 * участник говорит о себе («представитель истца по доверенности», «у защиты
 * ходатайство») и речь самого суда («суд определил», «заседание объявляется
 * открытым»).
 *
 * ⚠️ Модуль ПРЕДЛАГАЕТ, страница спрашивает. Уверенно подписанная чужой ролью
 * реплика хуже нейтрального «Говорящий 2» — она выглядит достоверной. Поэтому
 * роль отдаётся только при явном перевесе голосов, а всё сомнительное остаётся
 * неподписанным. Замеры — в docs/court-transcript.md.
 *
 * Только русский язык: правила стоят на русских процессуальных формулах.
 */
(function (root) {
    'use strict';

    /* Роль → как к ней обращаются. Порядок важен: «представитель истца» раньше
       «истец», иначе представителя запишут стороной. */
    var ROLES = [
        ['plaintiff_rep', 'представител[ьяю]\\s+(?:административного\\s+)?(?:истца|заявителя|взыскателя)'],
        ['defendant_rep', 'представител[ьяю]\\s+(?:административного\\s+)?(?:ответчика|должника)'],
        ['third_party',   '(?:представител[ьяю]\\s+)?трет(?:ье|ьего|ьему)\\s+лиц[оау]'],
        ['prosecutor',    'государственн(?:ый|ого|ому)\\s+обвинител[ьяю]|прокурор[ау]?|помощник[ау]?\\s+прокурора'],
        ['defender',      'защитник[ау]?|адвокат[ау]?'],
        ['accused',       'подсудим(?:ый|ая|ого|ой|ому)'],
        ['victim',        'потерпевш(?:ий|ая|его|ей|ему)'],
        ['witness',       'свидетел[ьяю]'],
        ['expert',        'эксперт[ау]?|специалист[ау]?'],
        ['secretary',     'секретар[ьяю]'],
        ['plaintiff',     'ист(?:ец|ца|цу|ица|ицы|ице)|заявител[ьяю]|взыскател[ьяю]'],
        ['defendant',     'ответчи(?:к|ка|ку|ца|цы|це)|должник[ау]?'],
    ];
    var ROLE_RE = ROLES.map(function (r) { return [r[0], new RegExp('^(?:' + r[1] + ')(?![а-яё])', 'i')]; });
    var ANY_ROLE = new RegExp('(?:' + ROLES.map(function (r) { return r[1]; }).join('|') + ')(?![а-яё])', 'i');

    /* Речь суда: формулы, которые произносит только председательствующий. */
    var JUDGE = [
        [/судебное\s+заседание\s+(?:[а-яё]+\s+){0,3}(?:объявляется|продолжается|открыт|закрыт)/i, 3],
        [/заседание\s+(?:объявляется\s+)?(?:открыт|закрыт)/i, 2],
        [/суд,?\s+(?:совещаясь\s+на\s+месте,?\s+)?(?:руководствуясь[^.]{0,60},?\s+)?(?:определил|постановил)/i, 3],
        [/(?:слушается|рассматривается)\s+(?:[а-яё]+\s+){0,2}дело/i, 2],
        [/вы\s+предупреждаетесь\s+об\s+(?:уголовной\s+)?ответственности/i, 3],
        [/отвод[ыа]?\s+(?:[а-яё]+\s+){0,4}име(?:ю|е)тся/i, 2],
        [/ходатайств[ао]\s+(?:[а-яё,]+\s+){0,6}име(?:ю|е)тся/i, 2],
        [/права\s+(?:[а-яё,]+\s+){0,3}(?:понятны|разъяснены|разъясняются)/i, 2],
        [/вам\s+разъясняются\s+права/i, 2],
        [/(?:пригласите|вызывается)\s+свидетел/i, 2],
        [/назовите\s+(?:вашу\s+)?фамилию/i, 2],
        [/суд\s+удаляется/i, 3],
        [/(?:^|[.!?]\s+)(?:свидетель|эксперт),?\s+вы\s+свободны/i, 2],
        [/(?:откладывается|отложить)\s+на\s/i, 1],
        /* Живая речь суда беднее формулами (замер на опубликованной расшифровке
           заседания 24.04.2018): распоряжения залу и вопросы «у кого ещё». */
        [/объявляется\s+перерыв/i, 2],
        [/(?:заносится|занести|запишите)\s+в\s+протокол|в\s+протоколе?\s+пишем/i, 2],
        [/(?:вопросы|ходатайства|дополнения|возражения|замечания)\s+у\s+[а-яё]+\s+(?:[а-яё]+\s+)?(?:есть|имеются)/i, 2],
        [/слово\s+предоставляется/i, 2],
        [/не\s+перебива(?:ть|йте)|я\s+запрещаю|я\s+вас\s+слушаю/i, 1],
    ];

    /* К суду обращаются, сам суд к себе — нет. Кто говорит «ваша честь» или
       «уважаемый суд», тот не судья; кто отвечает на такое обращение — скорее
       всего судья. */
    var TO_COURT = /ваша\s+честь|уважаемый\s+суд/i;

    /* Обращение в родительном или дательном: «Вопросы у прокурора ещё есть?»,
       «Слово предоставляется защитнику». */
    var OBLIQUE = [
        ['plaintiff_rep', 'представител[яю]\\s+(?:истца|заявителя)'],
        ['defendant_rep', 'представител[яю]\\s+ответчика'],
        ['prosecutor',    'государственного\\s+обвинителя|государственному\\s+обвинителю|прокурор[ау]|обвинени[яю]'],
        ['defender',      'защитник[ау]|защит[ые]|адвокат[ау]'],
        ['accused',       'подсудим(?:ого|ой|ому)'],
        ['victim',        'потерпевш(?:его|ей|ему)'],
        ['plaintiff',     'истц[ау]|истиц[ые]'],
        ['defendant',     'ответчик[ау]|ответчиц[ые]'],
    ];
    var OBLIQUE_RE = OBLIQUE.map(function (r) {
        return [r[0], new RegExp('(?:(?:вопросы|ходатайства|дополнения|возражения|замечания)\\s+у|слово\\s+предоставляется|слово)\\s+(?:' + r[1] + ')(?![а-яё])[^.!?]{0,30}[?.!]?$', 'i')];
    });
    /* Роль названа, но слова ей не дают: «Свидетель, вы свободны». */
    var DISMISS = /вы\s+свободны|можете\s+быть\s+свободны|присаживайтесь|садитесь|встаньте/i;

    /* Что участник говорит о себе и своей стороне. */
    var SELF = [
        ['plaintiff_rep', /представител[ья]\s+(?:административного\s+)?(?:истца|заявителя|взыскателя)\s+по\s+доверенности/i, 4],
        ['defendant_rep', /представител[ья]\s+(?:административного\s+)?(?:ответчика|должника)\s+по\s+доверенности/i, 4],
        ['plaintiff_rep', /(?:^|[.!?,]\s+)(?:я\s+)?представител[ья]\s+(?:истца|заявителя)(?![а-яё])/i, 2],
        ['defendant_rep', /(?:^|[.!?,]\s+)(?:я\s+)?представител[ья]\s+ответчика(?![а-яё])/i, 2],
        ['plaintiff_side', /(?:исковые\s+требования|иск|заявленные\s+требования)\s+(?:[а-яё]+\s+){0,2}поддерживаю|поддерживаю\s+(?:[а-яё]+\s+){0,3}(?:исковые\s+требования|иск)|^поддерживаю\s+(?:в\s+полном\s+объ[её]ме|с\s+уч[её]том)/i, 2],
        ['plaintiff_side', /заявляю\s+об\s+уточнении\s+исковых|прошу\s+взыскать/i, 1.5],
        ['defendant_side', /(?:иск|исковые\s+требования|с\s+иском|требования)\s+(?:[а-яё]+\s+){0,2}не\s+(?:признаю|призна[её]м|согласен|согласна|согласны)/i, 2],
        ['defendant_side', /заявляем?\s+о\s+снижении\s+неустойки|ответчик\s+не\s+оспаривает|мы\s+признаём\s+долг/i, 1.5],
        ['prosecutor',    /обвинение\s+поддерживаю|ходатайствую\s+об\s+оглашении\s+показаний|государственное\s+обвинение/i, 2.5],
        ['defender',      /(?:мой|моего|моему|моим)\s+подзащитн|у\s+защиты\s+(?:ходатайство|вопрос)|защита\s+(?:возражает|просит|полагает)/i, 2.5],
        ['accused',       /(?:согласен|согласна)\s+с\s+защитником|поддерживаю\s+(?:своего\s+)?защитника|вину\s+(?:не\s+)?признаю/i, 2],
        ['secretary',     /в\s+судебное\s+заседание\s+(?:явились|не\s+явил)|прошу\s+встать|пройдите\s+в\s+зал/i, 3],
    ];

    /* Признаки того, что запись — судебное заседание, а не совещание. */
    var HEARING = [
        /судебное\s+заседание/i, /суд,?\s+(?:совещаясь\s+на\s+месте,?\s+)?(?:определил|постановил)/i,
        /ходатайств/i, /(?:исковые\s+требования|истец|ответчик|подсудим|государственн[а-я]+\s+обвинител)/i,
        /отвод/i, /предупреждаетесь\s+об\s+(?:уголовной\s+)?ответственности/i, /материал[аы]м?\s+дела/i,
    ];

    function isSpeech(s) { return !!(s && !s.isParagraph && s.text); }

    /** Последнее предложение реплики и то, что в нём стоит до первой запятой. */
    function lastSentence(text) {
        var t = String(text || '').replace(/\s+/g, ' ').trim();
        /* Без «просмотра назад» в регулярном выражении: Safari до 16.4 его не
           знает, и модуль не разобрался бы целиком. */
        var parts = t.match(/[^.!?…]+[.!?…]*/g) || [t];
        return (parts[parts.length - 1] || '').trim();
    }

    /**
     * К кому обращается конец реплики. «Истец?», «Ответчик, ваше мнение?»,
     * «Представитель истца.», «От ответчика?» — да. «Заслушав истца, суд
     * определил…» — нет: роль названа, но к ней не обращаются.
     */
    function addressee(text) {
        var last = lastSentence(text);
        if (!last || DISMISS.test(last)) return null;
        var words = last.split(/\s+/).length;
        var head = last.replace(/^(?:от|со\s+стороны)\s+/i, '');
        var fromSide = head !== last;
        for (var o = 0; o < OBLIQUE_RE.length; o++) {
            if (OBLIQUE_RE[o][1].test(last)) return OBLIQUE_RE[o][0];
        }
        for (var i = 0; i < ROLE_RE.length; i++) {
            var m = ROLE_RE[i][1].exec(head);
            if (!m) continue;
            var rest = head.slice(m[0].length);
            /* После роли — конец, знак или запятая с коротким хвостом. */
            if (/^\s*(?:[А-ЯЁ][а-яё]+\s*)?[?.!]?$/.test(rest) || (/^\s*(?:[А-ЯЁ][а-яё]+)?\s*,/.test(rest) && words <= 14) || (fromSide && words <= 4)) {
                var key = ROLE_RE[i][0];
                /* «От истца кто?» — придёт сторона или её представитель. */
                if (fromSide && (key === 'plaintiff' || key === 'defendant')) key += '_side';
                return key;
            }
        }
        return null;
    }

    /* Одна улика весом от 3 — человек сам назвал своё положение («представитель
       истца по доверенности»). Её хватает одной; обращению суда нужно повториться. */
    var STRONG = 3;
    function add(votes, label, role, w, strong) {
        if (!label || !role) return;
        if (!votes[label]) votes[label] = {};
        votes[label][role] = (votes[label][role] || 0) + w;
        if (w >= STRONG && strong) strong[label + '|' + role] = 1;
    }

    /**
     * @param {Array} segs сегменты расшифровки с метками говорящих
     * @returns {{hearing:boolean, criminal:boolean, roles:Object<string,string>, votes:Object, judge:?string}}
     *          roles: метка → ключ роли; только уверенные
     */
    function detect(segs) {
        var speech = (segs || []).filter(isSpeech);
        var out = { hearing: false, criminal: false, roles: {}, votes: {}, judge: null, mixed: {} };
        if (speech.length < 4) return out;

        var all = speech.map(function (s) { return s.text; }).join(' ');
        var marks = 0;
        for (var h = 0; h < HEARING.length; h++) if (HEARING[h].test(all)) marks++;
        out.hearing = marks >= 3;
        out.criminal = /подсудим|уголовн[а-я]+\s+дел|государственн[а-я]+\s+обвинител/i.test(all);
        if (!out.hearing) return out;

        /* Реплики: подряд идущие сегменты одного голоса — одна реплика. */
        var turns = [];
        for (var i = 0; i < speech.length; i++) {
            var s = speech[i], lab = s.speaker || null;
            var prev = turns[turns.length - 1];
            if (prev && prev.label === lab) prev.text += ' ' + s.text;
            else turns.push({ label: lab, text: s.text });
        }

        /* 1. Судья — по речи суда. */
        var judgeScore = {};
        turns.forEach(function (t) {
            if (!t.label) return;
            for (var j = 0; j < JUDGE.length; j++) {
                if (JUDGE[j][0].test(t.text)) judgeScore[t.label] = (judgeScore[t.label] || 0) + JUDGE[j][1];
            }
        });
        var notJudge = {};
        for (var c = 0; c < turns.length; c++) {
            var tc = turns[c];
            if (!tc.label || !TO_COURT.test(tc.text)) continue;
            notJudge[tc.label] = (notJudge[tc.label] || 0) + 1;
            var after = turns[c + 1], before = turns[c - 1];
            if (after && after.label && after.label !== tc.label) judgeScore[after.label] = (judgeScore[after.label] || 0) + 1;
            if (before && before.label && before.label !== tc.label) judgeScore[before.label] = (judgeScore[before.label] || 0) + 0.5;
        }
        Object.keys(notJudge).forEach(function (l) { delete judgeScore[l]; });
        var ranked = Object.keys(judgeScore).sort(function (a, b) { return judgeScore[b] - judgeScore[a]; });
        if (ranked.length && judgeScore[ranked[0]] >= 4 && (ranked.length < 2 || judgeScore[ranked[0]] >= 2 * judgeScore[ranked[1]])) {
            out.judge = ranked[0];
        }

        var votes = out.votes, strong = {};
        /* 2. Обращение суда → кто ответил. 3. Что участник говорит о себе. */
        for (var k = 0; k < turns.length; k++) {
            var t = turns[k];
            if (!t.label) continue;
            var byJudge = out.judge ? (t.label === out.judge) : (judgeScore[t.label] || 0) > 0;
            if (byJudge) {
                var to = addressee(t.text), next = turns[k + 1];
                if (to && next && next.label && next.label !== t.label) add(votes, next.label, to, 2);
                /* «Пригласите свидетеля… Назовите фамилию» — представляется свидетель. */
                if (/назовите\s+(?:вашу\s+)?фамилию|представьтесь/i.test(lastSentence(t.text)) && next && next.label && next.label !== t.label) {
                    add(votes, next.label, /эксперт/i.test(t.text) ? 'expert' : 'witness', 2);
                }
                /* Об ответственности за ложные показания предупреждают того, кто
                   сейчас будет их давать. */
                if (/предупреждаетесь\s+об\s+(?:уголовной\s+)?ответственности/i.test(t.text) && next && next.label && next.label !== t.label) {
                    add(votes, next.label, /эксперт/i.test(t.text) ? 'expert' : (/потерпевш/i.test(t.text) ? 'victim' : 'witness'), 2);
                }
            } else {
                for (var q = 0; q < SELF.length; q++) {
                    if (SELF[q][1].test(t.text)) add(votes, t.label, SELF[q][0], SELF[q][2], strong);
                }
            }
        }

        /* 4. Решение по каждому голосу. */
        if (out.judge) out.roles[out.judge] = 'judge';
        Object.keys(votes).forEach(function (label) {
            if (label === out.judge) return;
            var v = votes[label], merged = {};
            /* «Сторона истца» отдаёт голоса и истцу, и представителю: кто из них
               говорит, решают остальные улики. Если их нет — это сторона лично
               (в районном суде чаще приходят сами). */
            Object.keys(v).forEach(function (r) {
                if (r === 'plaintiff_side') {
                    var rep = v.plaintiff_rep || 0;
                    merged[rep > 0 ? 'plaintiff_rep' : 'plaintiff'] = (merged[rep > 0 ? 'plaintiff_rep' : 'plaintiff'] || 0) + v[r];
                } else if (r === 'defendant_side') {
                    var rep2 = v.defendant_rep || 0;
                    var key = out.criminal ? 'accused' : (rep2 > 0 ? 'defendant_rep' : 'defendant');
                    merged[key] = (merged[key] || 0) + v[r];
                } else {
                    merged[r] = (merged[r] || 0) + v[r];
                }
            });
            /* Представитель, к которому суд обращался «Истец?»: это один человек. */
            if (merged.plaintiff_rep && merged.plaintiff) { merged.plaintiff_rep += merged.plaintiff; delete merged.plaintiff; }
            if (merged.defendant_rep && merged.defendant) { merged.defendant_rep += merged.defendant; delete merged.defendant; }
            var order = Object.keys(merged).sort(function (a, b) { return merged[b] - merged[a]; });
            if (!order.length) return;
            var best = merged[order[0]], second = order.length > 1 ? merged[order[1]] : 0;
            /* Одного обращения мало: на вопрос суду прокурору может ответить
               защитник, и запись бывает неполной (на опубликованной расшифровке
               24.04.2018 единственное «вопросы у прокурора есть?» отдало роль
               защитнику). Нужны два независимых случая — или собственные слова
               участника о своём положении. */
            var sure = best >= 4 || (best >= STRONG && (strong[label + '|' + order[0]] || strong[label + '|' + order[0].replace(/(_rep)?$/, '_side')]));
            /* У голоса сильные улики за ДВЕ роли — значит, разметка слила двоих
               (замер 07.10.2026: уголовное заседание, прокурор и свидетель — обе
               женщины — получили одну метку; правило «в полтора раза больше»
               подписало бы прокурора свидетелем). Такому голосу роль не даётся
               вовсе, а страница говорит, кого в нём двое. */
            if (sure && second >= 2 && best < 3 * second) { out.mixed[label] = [order[0], order[1]]; return; }
            if (sure && best >= 1.5 * second) out.roles[label] = order[0];
        });
        return out;
    }

    /**
     * Подписи для найденных ролей. `titles` — словарь страницы: ключ роли →
     * слово («Судья»). Двум свидетелям даём номера: это разные люди, и одно
     * имя слило бы их в одного говорящего.
     * @returns {Array<{label:string, role:string, name:string}>}
     */
    function propose(segs, titles) {
        var d = detect(segs), list = [], count = {}, seen = {};
        if (!d.hearing) return { hearing: false, court: false, items: [], mixed: [] };
        var labels = [];
        (segs || []).forEach(function (s) { if (isSpeech(s) && s.speaker && !seen[s.speaker]) { seen[s.speaker] = 1; labels.push(s.speaker); } });
        labels.forEach(function (l) { var r = d.roles[l]; if (r) count[r] = (count[r] || 0) + 1; });
        var idx = {};
        labels.forEach(function (l) {
            var r = d.roles[l];
            if (!r) return;
            var name = (titles && titles[r]) || r;
            if ((r === 'witness' || r === 'expert' || r === 'third_party' || r === 'victim') && count[r] > 1) {
                idx[r] = (idx[r] || 0) + 1;
                name += ' ' + idx[r];
            }
            if (name !== l) list.push({ label: l, role: r, name: name });
        });
        var mixed = [];
        labels.forEach(function (l) {
            if (!d.mixed[l]) return;
            mixed.push({ label: l, roles: d.mixed[l].map(function (r) { return (titles && titles[r]) || r; }) });
        });
        /* `court` — в записи слышен сам суд. Консультация юриста с клиентом полна
           тех же слов («ходатайство», «истец», «заседание»), но заседанием не
           является: без голоса суда страница в судебный режим не переходит. */
        return { hearing: true, court: !!d.judge, criminal: d.criminal, items: list, mixed: mixed };
    }

    root.__transcriptRoles = { detect: detect, propose: propose, addressee: addressee };
})(typeof window !== 'undefined' ? window : globalThis);
